<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminInquiryList" label="Inquiries" :active="true" />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Inquiry List</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-switch :left-label="true" v-model="showErrors">Show All Inquiries</b-switch>
          </div>
        </div>
      </div>
      <hr />

      <!-- Filter Section -->
      <div class="filter-section">
        <div class="columns is-multiline">
          <div class="column is-3">
            <b-field label="Organization">
              <b-select
                v-model="selectedOrganizationID"
                placeholder="All Organizations"
                :loading="loadingOrganizations"
                @input="handleOrganizationChange"
                expanded
              >
                <option :value="0">All Organizations</option>
                <option v-for="org in organizationOptions" :value="org.id" :key="org.id">
                  {{ org.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field label="Provider">
              <b-select
                v-model="selectedProviderID"
                placeholder="All Providers"
                :loading="loadingProviders"
                :disabled="selectedOrganizationID === 0"
                @input="handleProviderChange"
                expanded
              >
                <option :value="0">All Providers</option>
                <option v-for="provider in providerOptions" :value="provider.id" :key="provider.id">
                  {{ provider.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field label="User">
              <b-select
                v-model="selectedUserID"
                placeholder="All Users"
                :loading="loadingUsers"
                :disabled="selectedOrganizationID === 0"
                @input="handleUserChange"
                expanded
              >
                <option :value="0">All Users</option>
                <option v-for="user in userOptions" :value="user.id" :key="user.id">
                  {{ user.first_name }} {{ user.last_name }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>

      <div>
        <InquiryTable
          :options="options"
          :isForAdmin="true"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-items="totalItems"
          :loading="loading"
          @page-change="handlePageChange"
          @search-changed="handleSearchChange"
        />
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import InquiryTable from '../../components/InquiryTable.vue';
import AdminLayout from '../../layouts/AdminLayout.vue';
import request from '../../request';

export default {
  name: 'AdminInquiryList',
  components: {
    InquiryTable,
    AdminLayout,
  },
  data() {
    return {
      loading: true,
      options: [],
      selectedSince: 7,
      showErrors: false,
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      selectedStatus: ['pending', 'complete', 'max-retry', 'unknown', 'expired', 'cancel', 'draft'],
      search: null,
      searchableFields: [],
      // Filter options
      selectedOrganizationID: 0,
      selectedProviderID: 0,
      selectedUserID: 0,
      organizationOptions: [],
      providerOptions: [],
      userOptions: [],
      loadingOrganizations: false,
      loadingProviders: false,
      loadingUsers: false,
    };
  },
  mounted() {
    this.fetchOrganizations();
    this.fetchInquiries();
  },
  methods: {
    async fetchOrganizations() {
      this.loadingOrganizations = true;
      try {
        const resp = await request('/admin/organizations');
        this.organizationOptions = resp.data.organizations.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load organizations.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingOrganizations = false;
      }
    },
    async fetchProviders(organizationId) {
      this.loadingProviders = true;
      this.providerOptions = [];
      this.selectedProviderID = 0;

      if (organizationId === 0) {
        this.loadingProviders = false;
        return;
      }

      try {
        const resp = await request(`/admin/providers?organization_id=${organizationId}`);
        this.providerOptions = resp.data.providers.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load providers.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingProviders = false;
      }
    },
    async fetchUsers(organizationId) {
      this.loadingUsers = true;
      this.userOptions = [];
      this.selectedUserID = 0;

      if (organizationId === 0) {
        this.loadingUsers = false;
        return;
      }

      try {
        const resp = await request(`/admin/users?organization_id=${organizationId}`);
        this.userOptions = resp.data.users.sort((a, b) => a.first_name.localeCompare(b.first_name));
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load users.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    handleOrganizationChange() {
      this.fetchProviders(this.selectedOrganizationID);
      this.fetchUsers(this.selectedOrganizationID);
      this.currentPage = 1;
      this.fetchInquiries();
    },
    handleProviderChange() {
      this.currentPage = 1;
      this.fetchInquiries();
    },
    handleUserChange() {
      this.currentPage = 1;
      this.fetchInquiries();
    },
    handleSelect() {
      this.loading = true;
      this.fetchInquiries().finally(() => (this.loading = false));
    },
    fetchInquiries() {
      let args = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      // Add filter parameters
      if (this.selectedOrganizationID !== 0) {
        args.organization_id = this.selectedOrganizationID;
      }
      if (this.selectedProviderID !== 0) {
        args.provider_id = this.selectedProviderID;
      }
      if (this.selectedUserID !== 0) {
        args.user_id = this.selectedUserID;
      }

      // Only apply status filter if showErrors is false
      if (!this.showErrors && this.selectedStatus && this.selectedStatus.length > 0) {
        args['status'] = this.selectedStatus.join(',');
      }
      // Add search parameters if they exist
      if (this.search && this.searchableFields && this.searchableFields.length > 0) {
        this.searchableFields.forEach((field) => {
          args[field] = this.search;
        });
      }

      let params = new URLSearchParams(args).toString();
      return request(`/admin/inquiries/?${params}`)
        .then((resp) => {
          this.options = resp.data.inquiries;
          this.totalItems = Number(resp.headers['x-total-count']);
          this.loading = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: 'An error occurred while fetching inquiries.',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchInquiries();
    },
    handleSearchChange({ search, fields }) {
      this.search = search;
      this.searchableFields = fields;
      this.currentPage = 1; // Reset to first page when search changes
      this.fetchInquiries();
    },
  },
  watch: {
    showErrors() {
      this.fetchInquiries();
    },
  },
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
}
</style>
